import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import './marketEvents.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendar } from '@fortawesome/free-solid-svg-icons';

function fmtDateLabel(d) {
  if (!d) return '';
  // Use noon so an ISO day key keeps its calendar day in every browser timezone.
  const dt = typeof d === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(d)
    ? new Date(`${d}T12:00:00Z`) : new Date(d);
  return dt.toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    timeZone: 'Europe/Vilnius',
  });
}

function fmtDateInput(d) {
  if (!d) return '';
  const dt = typeof d === 'string' ? new Date(d) : d;
  const year = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const day = String(dt.getDate()).toString().padStart(2, '0');
  return `${year}-${m}-${day}`;
}

function parseDateInput(value) {
  if (!value) return null;
  const d = new Date(value + 'T00:00:00Z');
  return Number.isNaN(d.getTime()) ? null : d;
}

function isGoodData(ev) {
  return (ev.quality || '').toLowerCase().includes('good');
}
function isBadData(ev) {
  return (ev.quality || '').toLowerCase().includes('bad');
}


function groupByDate(events) {
  const map = new Map();
  for (const ev of events) {
    const dt = ev.date ? new Date(ev.date) : null;
    if (!dt || Number.isNaN(dt.getTime())) continue;
    const key = dt.toLocaleDateString('sv-SE', { timeZone: 'Europe/Vilnius' });
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(ev);
  }
  return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
}

/** ---- helpers for filters ---- **/

// classify impact into high/medium/low
function classifyImpact(ev) {
  const impact = (ev.impact || '').toLowerCase();
  if (['high', 'medium', 'low'].includes(impact)) return impact;
  const strength = (ev.strength || '').toLowerCase();

  // map common strings from the API
  if (strength.includes('strong')) return 'high';
  if (strength.includes('high')) return 'high';
  if (strength.includes('medium')) return 'medium';
  if (strength.includes('weak')) return 'low';
  if (strength.includes('low')) return 'low';

  // fall back on name keywords for high-impact data
  const name = (ev.name || '').toLowerCase();
  const HIGH_KEYWORDS = [
    'nonfarm payroll',
    'unemployment rate',
    'cpi',
    'consumer price',
    'core cpi',
    'pce',
    'interest rate',
    'rate decision',
    'fomc',
    'gdp',
    'gross domestic product',
    'ism',
    'pmi',
    'retail sales',
    'consumer sentiment',
  ];
  if (HIGH_KEYWORDS.some(k => name.includes(k))) return 'high';

  return 'medium'; // default
}

// classify region from currency
function classifyRegion(ev) {
  const cur = (ev.currency || '').toUpperCase();
  if (cur === 'USD') return 'us';
  if (cur === 'EUR') return 'eu';
  return 'other';
}

function impactClass(ev) {
  const base = 'ts-econ-impact';
  const level = classifyImpact(ev);

  if (level === 'high') return `${base} ts-econ-impact-high`;
  if (level === 'low') return `${base} ts-econ-impact-low`;
  if (level === 'medium') return `${base} ts-econ-impact-med`;
  return `${base} ts-econ-impact-med`;
}

export default function EconomicCalendar({
  initialDays = 7,
  maxDays = 30,
}) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const [rangePreset, setRangePreset] = useState(
    initialDays <= 3 ? '3d' : initialDays <= 7 ? '7d' : '30d'
  );

  const [fromDate, setFromDate] = useState(() => today);
  const [toDate, setToDate] = useState(() => {
    const d = new Date(today);
    d.setDate(d.getDate() + initialDays);
    return d;
  });

  // NEW: impact + region filters
  const [impactFilter, setImpactFilter] = useState('high'); // 'high' | 'medium' | 'low' | 'all'
  const [geoFilter, setGeoFilter] = useState('all');        // 'us' | 'eu' | 'other' | 'all'
  const [snapshotId, setSnapshotId] = useState(null);
  const userId = localStorage.getItem('userId');
  const isAdmin = userId === 'pziPDtOgJiX06oGuJxNwxE';

  const [editingId, setEditingId] = useState(null);
  const [editActual, setEditActual] = useState('');

  const isLocal = window.location.hostname === 'localhost';
  const API_BASE = isLocal
    ? 'http://localhost:5000'
    : 'https://www.trendscout.club';

  // Fetch wide window once (up to maxDays) and then filter client-side
  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);
      try {
        const res = await axios.get(
          `${API_BASE}/api/market-events/economic-events`,
          {
            params: { days: maxDays },
          }
        );

        if (!cancelled) {
          setSnapshotId(res.data?.snapshotId || null);
          const evs = (res.data?.events || []).map((ev, i) => ({
            ...ev,
            _id: ev._id || ev.id || `${i}-${ev.name}-${ev.rawDate}`,
          }));
          setEvents(evs);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e.message || 'Failed to load macro events');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [API_BASE, maxDays]);

  // Apply date + impact + region filters
  const filteredEvents = useMemo(() => {
    if (!events.length) return [];

    const from = fromDate ? new Date(fromDate) : null;
    const to = toDate ? new Date(toDate) : null;
    if (from) from.setHours(0, 0, 0, 0);
    if (to) to.setHours(23, 59, 59, 999);

    return events.filter(ev => {
      if (!ev.date) return false;
      const d = new Date(ev.date);
      if (Number.isNaN(d.getTime())) return false;
      if (from && d < from) return false;
      if (to && d > to) return false;

      // impact filter
      const impact = classifyImpact(ev); // high/medium/low/unknown->medium
      if (impactFilter !== 'all' && impact !== impactFilter) return false;

      // geo filter
      const region = classifyRegion(ev); // us/eu/other
      if (geoFilter !== 'all' && region !== geoFilter) return false;

      return true;
    });
  }, [events, fromDate, toDate, impactFilter, geoFilter]);

  const grouped = useMemo(
    () => groupByDate(filteredEvents),
    [filteredEvents]
  );

  async function saveActual(ev) {
  if (!snapshotId) return;
  // A blank field clears an actual. Number('') would silently confirm a false zero.
  const value = editActual.trim() === '' ? null : Number(editActual);
  if (value !== null && !Number.isFinite(value)) return;

  try {
    await axios.post(`${API_BASE}/api/market-events/economic-events/update-actual`, {
      snapshotId,
      name: ev.name,
      rawDate: ev.rawDate,
      actual: value,
    });

    // Reload the server's confirmed status and recalculated surprise together.
    try {
      const res = await axios.get(`${API_BASE}/api/market-events/economic-events`);
      setSnapshotId(res.data?.snapshotId || null);
      setEvents((res.data?.events || []).map((item, i) => ({
        ...item,
        _id: item._id || item.id || `${i}-${item.name}-${item.rawDate}`,
      })));
    } catch (refreshError) {
      // The POST succeeded. Never leave an old surprise beside the new actual.
      setEvents(prev => prev.map(item => item._id === ev._id
        ? { ...item, actual: value, hasActual: value !== null,
            status: value === null ? 'awaiting_actual' : 'released',
            surprisePct: null, surpriseBasis: null }
        : item));
      setError('Actual saved. Refresh the page to load its updated surprise.');
    }
    setEditingId(null);
    setEditActual('');
  } catch (e) {
    console.error('Failed to update actual', e);
    alert('Failed to update value');
  }
}

const surpriseEvents = useMemo(() => {
  if (!events.length) return [];

  const now = new Date();
  const cutoff = new Date(now.getTime() - 24 * 60 * 60 * 1000); // last 24h

  return events
    .filter(ev => {
      if (!ev.date) return false;
      const d = new Date(ev.date);
      return (
        (impactFilter === 'all' || classifyImpact(ev) === impactFilter) &&
        (geoFilter === 'all' || classifyRegion(ev) === geoFilter) &&
        ev.actual != null &&
        ev.surpriseBasis === 'forecast' &&
        typeof ev.surprisePct === 'number' &&
        Number.isFinite(ev.surprisePct) &&
        d >= cutoff && d <= now
      );
    })
    .sort((a, b) => new Date(a.date) - new Date(b.date));
}, [events, impactFilter, geoFilter]);

  // Preset range buttons
  function setPreset(preset) {
    setRangePreset(preset);
    const base = new Date(today);
    let span = 7;
    if (preset === '3d') span = 3;
    if (preset === '7d') span = 7;
    if (preset === '30d') span = 30;

    const from = new Date(base);
    const to = new Date(base);
    to.setDate(to.getDate() + span);
    setFromDate(from);
    setToDate(to);
  }

  function onFromInputChange(e) {
    const v = parseDateInput(e.target.value);
    if (!v) return;
    setRangePreset(null);
    setFromDate(v);
  }

  function onToInputChange(e) {
    const v = parseDateInput(e.target.value);
    if (!v) return;
    setRangePreset(null);
    setToDate(v);
  }

  
  return (
    <div className="ts-econ-card">
      <div className="ts-econ-header">
      <div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <h3 className="ts-econ-title" style={{ margin: 0 }}>
            Macro Event Calendar
          </h3>

          <FontAwesomeIcon
            icon={faCalendar}
            className="ts-econ-ff-link"
            title="Open ForexFactory calendar"
            onClick={() =>
              window.open(
                'https://www.forexfactory.com/calendar?utm_source=chatgpt.com',
                '_blank',
                'noopener,noreferrer'
              )
            }
          />
        </div>

        <p className="ts-econ-subtitle">
          High-signal releases (jobs, CPI, central bank speeches) that can
          impact crypto risk-taking.
        </p>
      </div>


        <div className="ts-econ-filters">
          {/* date range presets + pickers */}
          <div className="ts-econ-top-filters">
            <div className="ts-econ-chip-group">
              <button
                type="button"
                className={
                  'ts-econ-chip' +
                  (rangePreset === '3d' ? ' ts-econ-chip-active' : '')
                }
                onClick={() => setPreset('3d')}
              >
                3d
              </button>
              <button
                type="button"
                className={
                  'ts-econ-chip' +
                  (rangePreset === '7d' ? ' ts-econ-chip-active' : '')
                }
                onClick={() => setPreset('7d')}
              >
                7d
              </button>
              <button
                type="button"
                className={
                  'ts-econ-chip' +
                  (rangePreset === '30d' ? ' ts-econ-chip-active' : '')
                }
                onClick={() => setPreset('30d')}
              >
                30d
              </button>
            </div>
            <div className="ts-econ-date-range">
              <label>
                <span>From</span>
                <input
                  type="date"
                  value={fmtDateInput(fromDate)}
                  onChange={onFromInputChange}
                />
              </label>
              <label>
                <span>To</span>
                <input
                  type="date"
                  value={fmtDateInput(toDate)}
                  onChange={onToInputChange}
                />
              </label>
            </div>
          </div>

          {/* NEW: impact + geography filters */}
          <div className="ts-econ-extra-filters">
            <div className="ts-econ-chip-group ts-econ-chip-group-sub">
              <span className="ts-econ-filter-label">Impact:</span>
              <button
                type="button"
                className={
                  'ts-econ-chip ts-econ-chip-sm' +
                  (impactFilter === 'high' ? ' ts-econ-chip-active' : '')
                }
                onClick={() => setImpactFilter('high')}
              >
                High
              </button>
              <button
                type="button"
                className={
                  'ts-econ-chip ts-econ-chip-sm' +
                  (impactFilter === 'medium' ? ' ts-econ-chip-active' : '')
                }
                onClick={() => setImpactFilter('medium')}
              >
                Med
              </button>
              <button
                type="button"
                className={
                  'ts-econ-chip ts-econ-chip-sm' +
                  (impactFilter === 'low' ? ' ts-econ-chip-active' : '')
                }
                onClick={() => setImpactFilter('low')}
              >
                Low
              </button>
              <button
                type="button"
                className={
                  'ts-econ-chip ts-econ-chip-sm' +
                  (impactFilter === 'all' ? ' ts-econ-chip-active' : '')
                }
                onClick={() => setImpactFilter('all')}
              >
                All
              </button>
            </div>

            <div className="ts-econ-chip-group ts-econ-chip-group-sub">
              <span className="ts-econ-filter-label">Region:</span>
              <button
                type="button"
                className={
                  'ts-econ-chip ts-econ-chip-sm' +
                  (geoFilter === 'us' ? ' ts-econ-chip-active' : '')
                }
                onClick={() => setGeoFilter('us')}
              >
                US
              </button>
              <button
                type="button"
                className={
                  'ts-econ-chip ts-econ-chip-sm' +
                  (geoFilter === 'eu' ? ' ts-econ-chip-active' : '')
                }
                onClick={() => setGeoFilter('eu')}
              >
                Europe
              </button>
              <button
                type="button"
                className={
                  'ts-econ-chip ts-econ-chip-sm' +
                  (geoFilter === 'other' ? ' ts-econ-chip-active' : '')
                }
                onClick={() => setGeoFilter('other')}
              >
                Others
              </button>
              <button
                type="button"
                className={
                  'ts-econ-chip ts-econ-chip-sm' +
                  (geoFilter === 'all' ? ' ts-econ-chip-active' : '')
                }
                onClick={() => setGeoFilter('all')}
              >
                All
              </button>
            </div>
          </div>
        </div>
      </div>

      {loading && <div className="ts-econ-status">Loading macro events…</div>}
      {error && <div className="ts-econ-error">{error}</div>}

      {!loading && !error && grouped.length === 0 && (
        <div className="ts-econ-empty">
          No macro events found for the selected range / filters.
        </div>
      )}

      {/* Compact surprise-only list */}
      {surpriseEvents.length > 0 && (
        <div className="ts-econ-surprise-panel">
          <div className="ts-econ-surprise-header">
            <h4>Surprises (last 24h)</h4>
            <span className="ts-econ-surprise-count">
              {surpriseEvents.length} events with actual vs forecast
            </span>
          </div>

          <div className="ts-econ-body">
            {surpriseEvents.map(ev => (
              <div key={ev._id} className="ts-econ-surprise-row">
                <div className="ts-econ-surprise-date">
                  {ev.date
                    ? new Date(ev.date).toLocaleString('lt-LT', {
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                        timeZone: 'Europe/Vilnius',
                      })
                    : '—'}
                </div>
                <div className="ts-econ-surprise-name">
                  {ev.name}
                </div>
                <div
                  className={
                    'ts-econ-surprise-pct ' +
                    (isGoodData(ev)
                      ? 'ts-econ-surprise-good'
                      : isBadData(ev)
                      ? 'ts-econ-surprise-bad'
                      : '')
                  }
                >
                  {ev.surprisePct >= 0 ? '+' : ''}
                  {ev.surprisePct.toFixed(1)}%
                </div>
              </div>
            ))}
          </div>
        </div>
      )}


      <div className="ts-econ-body">
        {grouped.map(([dateKey, items]) => (
          <div key={dateKey} className="ts-econ-day">
            <div className="ts-econ-day-header">
              <span className="ts-econ-day-dot" />
              <span className="ts-econ-day-label">
                {fmtDateLabel(dateKey)}
              </span>
            </div>
            <ul className="ts-econ-list">
            {items
              .sort((a, b) => String(a.date).localeCompare(String(b.date)))
              .map(ev => {
                // 👇 JS land, before returning JSX
                const isSpeech =
                  /\b(speech|speaks|testimony|press conference)\b/i.test(ev.name || '') &&
                  ev.actual == null &&
                  (ev.forecast == null || ev.forecast === 0) &&
                  (ev.previous == null || ev.previous === 0);

                return (
                  <li key={ev._id} className="ts-econ-item">
                    <div className="ts-econ-time">
                      {ev.date
                        ? new Date(ev.date).toLocaleTimeString('lt-LT', {
                            hour: '2-digit',
                            minute: '2-digit',
                            timeZone: 'Europe/Vilnius',
                          })
                        : '—'}
                    </div>

                    <div className="ts-econ-main">
                      <div className="ts-econ-event">{ev.name}</div>
                      <div className="ts-econ-meta">
                        {ev.currency && (
                          <span className="ts-econ-currency">
                            {ev.currency}
                          </span>
                        )}
                        <span className={impactClass(ev)}>
                          {ev.strength && ev.strength !== 'Data Not Loaded'
                            ? ev.strength
                            : classifyImpact(ev) === 'high'
                            ? 'High impact'
                            : classifyImpact(ev) === 'medium'
                            ? 'Medium impact'
                            : 'Low impact'}
                        </span>
                      </div>
                    </div>

                    <div className="ts-econ-values">
                      {/* Show confirmed zero, but leave unreleased values blank. */}
                      {ev.actual != null && (
                        <span className="ts-econ-val">
                          Act: <b>{ev.actual}</b>
                        </span>
                      )}
                      {ev.status === 'awaiting_actual' && !isSpeech && (
                        <span className="ts-econ-val">Actual pending</span>
                      )}

                      {/* Speech labels depend on the event, not a zero result. */}
                      {isSpeech ? (
                        <span className="ts-econ-speech">SPEECH</span>
                      ) : (
                        <>
                          {ev.forecast != null && (
                            <span className="ts-econ-val">
                              F: <b>{ev.forecast}</b>
                            </span>
                          )}
                          {ev.previous != null && (
                            <span className="ts-econ-val">
                              Prev: {ev.previous}
                            </span>
                          )}
                        </>
                      )}

                      {/* surprise % chip (if you’re using it) */}
                      {typeof ev.surprisePct === 'number' && (
                        <span
                          className={
                            'ts-econ-surprise ' +
                            (isGoodData(ev)
                              ? 'ts-econ-surprise-good'
                              : isBadData(ev)
                              ? 'ts-econ-surprise-bad'
                              : '')
                          }
                        >
                          {ev.surpriseBasis === 'forecast' ? 'vs F:' : 'vs Prev:'}{' '}
                          {ev.surprisePct >= 0 ? '+' : ''}
                          {ev.surprisePct.toFixed(1)}%
                        </span>
                      )}
                    </div>

                    {isAdmin && ev.forecast != null && (
                      <div className="ts-econ-admin-edit">
                        {editingId === ev._id ? (
                          <>
                            <input
                              type="number"
                              step="0.1"
                              value={editActual}
                              onChange={e => setEditActual(e.target.value)}
                            />
                            <button onClick={() => saveActual(ev)}>Save</button>
                            <button onClick={() => setEditingId(null)}>X</button>
                          </>
                        ) : (
                          <button
                            className="ts-econ-edit-btn"
                            onClick={() => {
                              setEditingId(ev._id);
                              setEditActual(
                                ev.actual != null ? String(ev.actual) : ''
                              );
                            }}
                          >
                            Edit
                          </button>
                        )}
                      </div>
                    )}
                  </li>
                );
              })}

            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}
